/**
 * GroupDashboardPage — Ebene 1: InvictaX Gruppe (Marketing OS).
 * Aggregierte KPIs ueber alle Mandanten + Firmen-Breakdown mit
 * Standorten/Geschaeftsbereichen (Ebene 3) und Ads-Daten (best-effort).
 */
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Network, Loader2, Coins, UserCheck, Send, Heart, Building2,
  ChevronDown, ChevronRight, Target, RefreshCw, MapPin, MousePointerClick, Mail, Trophy, TrendingUp,
} from "lucide-react";
import PageHelp from "@/components/PageHelp";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtMoney = (n) =>
  n === null || n === undefined ? "—" : `CHF ${Number(n).toLocaleString("de-CH", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtNum = (n) => (n === null || n === undefined ? "—" : Number(n).toLocaleString("de-CH"));

function GroupKpi({ testid, icon: Icon, label, value, sub }) {
  return (
    <Card data-testid={testid}>
      <CardContent className="pt-4 pb-3 px-4">
        <div className="flex items-center gap-2 text-xs text-[var(--studio-text-muted)]">
          <Icon className="w-3.5 h-3.5" /> {label}
        </div>
        <div className="text-xl font-bold mt-1">{value}</div>
        {sub && <div className="text-[11px] text-[var(--studio-text-muted)] mt-0.5">{sub}</div>}
      </CardContent>
    </Card>
  );
}

function CompanyRow({ r, maxSpend }) {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);
  const spend = (r.ads?.spend || 0) + (r.google_spend || 0);
  const barPct = maxSpend > 0 ? Math.max((spend / maxSpend) * 100, spend > 0 ? 4 : 0) : 0;
  return (
    <div className="border border-[var(--studio-border)] rounded-lg" data-testid={`group-company-${r.company_id}`}>
      <div className="p-3 flex flex-wrap items-center gap-3">
        <button onClick={() => setExpanded((v) => !v)} className="shrink-0" data-testid={`expand-group-company-${r.company_id}`}>
          {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        </button>
        <div
          className="w-8 h-8 rounded-lg flex items-center justify-center text-white text-xs font-bold shrink-0"
          style={{ backgroundColor: r.brand_color || "#0F0F0F" }}
        >
          {r.logo_url ? <img src={r.logo_url} alt="" className="w-8 h-8 rounded-lg object-cover" /> : r.name.slice(0, 2).toUpperCase()}
        </div>
        <div className="flex-1 min-w-[150px]">
          <div className="font-medium text-sm flex items-center gap-2">
            {r.name}
            {!r.meta_connected && <Badge variant="outline" className="text-[9px] text-amber-600 border-amber-300">Meta nicht verbunden</Badge>}
          </div>
          <div className="flex flex-wrap gap-x-4 gap-y-0.5 text-[11px] text-[var(--studio-text-muted)]">
            <span>📤 {fmtNum(r.published)} publiziert</span>
            <span>🗓 {fmtNum(r.scheduled)} geplant</span>
            <span>❤️ {fmtNum(r.engagement)} Engagement</span>
            {r.ads && <span className="text-emerald-600">💰 Meta {fmtMoney(r.ads.spend)}{r.ads.leads > 0 ? ` · ${r.ads.leads} Leads` : ""}</span>}
            {r.google_spend > 0 && <span className="text-[#2563EB]">🔍 Google {fmtMoney(r.google_spend)}</span>}
            {r.form_leads > 0 && <span className="text-emerald-600">📝 {r.form_leads} Formular-Leads</span>}
            {r.revenue > 0 && (
              <span className="text-emerald-700 font-medium">
                🏆 {r.won_leads} Aufträge · {fmtMoney(r.revenue)}{r.roas ? ` · ROAS ${Number(r.roas).toFixed(2)}×` : ""}
              </span>
            )}
          </div>
          {barPct > 0 && (
            <div className="h-1 mt-1.5 rounded bg-[var(--studio-bg)] overflow-hidden max-w-xs">
              <div className="h-full rounded bg-[#2563EB]" style={{ width: `${barPct}%` }} />
            </div>
          )}
        </div>
        <Button variant="outline" size="sm" className="h-7 text-xs gap-1" onClick={() => navigate("/ads-manager")} data-testid={`group-goto-ads-${r.company_id}`}>
          <Target className="w-3 h-3" /> Ads
        </Button>
      </div>
      {expanded && (
        <div className="border-t border-[var(--studio-border)] p-3 bg-[var(--studio-bg)]" data-testid={`group-units-${r.company_id}`}>
          <div className="text-xs font-semibold mb-1.5 uppercase tracking-wide text-[var(--studio-text-muted)] flex items-center gap-1">
            <MapPin className="w-3 h-3" /> Standorte / Geschäftsbereiche
          </div>
          {r.business_units?.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {r.business_units.map((u) => (
                <Badge key={u.id} variant="outline" className="text-xs gap-1" data-testid={`group-unit-${u.id}`}>
                  {u.name} <span className="text-[var(--studio-text-muted)]">· {u.content_count} Posts</span>
                </Badge>
              ))}
            </div>
          ) : (
            <div className="text-xs text-[var(--studio-text-muted)]">
              Keine Standorte/Bereiche angelegt — unter <strong>Firmen → Standorte</strong> hinzufügen (z.B. Wetzikon, Rapperswil).
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function WeeklyReportCard() {
  const [settings, setSettings] = useState(null);
  const [recipients, setRecipients] = useState("");
  const [sendTime, setSendTime] = useState("07:00");
  const [busy, setBusy] = useState("");
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    axios.get(`${API}/group/weekly-report/settings`, { withCredentials: true })
      .then((r) => {
        setSettings(r.data);
        setRecipients((r.data.recipients || []).join(", "));
        setSendTime(r.data.send_time || "07:00");
      })
      .catch(() => setHidden(true));
  }, []);

  const save = async (patch) => {
    setBusy("save");
    try {
      const body = {
        recipients: recipients.split(",").map((s) => s.trim()).filter(Boolean),
        send_time: sendTime,
        ...patch,
      };
      const r = await axios.put(`${API}/group/weekly-report/settings`, body, { withCredentials: true });
      setSettings(r.data);
      toast.success("Report-Einstellungen gespeichert");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally { setBusy(""); }
  };

  const sendNow = async () => {
    setBusy("send");
    try {
      const r = await axios.post(`${API}/group/weekly-report/send`, {}, { withCredentials: true });
      toast.success(r.data.message);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Versand fehlgeschlagen");
    } finally { setBusy(""); }
  };

  if (hidden || !settings) return null;
  return (
    <Card data-testid="weekly-report-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex flex-wrap items-center gap-2">
          <Mail className="w-4 h-4 text-[#2563EB]" /> Wochen-Report (Montags per E-Mail)
          {settings.last_sent_at && (
            <span className="text-[10px] font-normal text-[var(--studio-text-muted)]">
              Zuletzt gesendet: {new Date(settings.last_sent_at).toLocaleString("de-CH")}
            </span>
          )}
          <div className="flex-1" />
          <div className="flex items-center gap-2 text-xs font-normal">
            Aktiv
            <Switch data-testid="weekly-report-switch" checked={settings.enabled}
              onCheckedChange={(v) => save({ enabled: v })} />
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent className="flex flex-wrap items-center gap-2">
        <Input
          data-testid="weekly-report-recipients"
          className="flex-1 min-w-[240px] h-8 text-xs"
          placeholder="Empfänger, kommagetrennt (leer = alle Admins)"
          value={recipients}
          onChange={(e) => setRecipients(e.target.value)}
        />
        <Input
          data-testid="weekly-report-time"
          type="time" className="w-28 h-8 text-xs"
          value={sendTime} onChange={(e) => setSendTime(e.target.value)}
        />
        <Button size="sm" className="h-8" disabled={busy === "save"} onClick={() => save({})} data-testid="weekly-report-save-btn">
          {busy === "save" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Speichern"}
        </Button>
        <Button size="sm" variant="outline" className="h-8 gap-1" disabled={busy === "send"} onClick={sendNow} data-testid="weekly-report-send-btn">
          {busy === "send" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} Jetzt senden
        </Button>
      </CardContent>
    </Card>
  );
}

function AdSpendCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    axios.get(`${API}/cockpit/group/ad-spend`, { withCredentials: true })
      .then((r) => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);
  const max = Math.max(...(data?.companies || []).map((c) => c.total), 1);
  return (
    <Card data-testid="group-adspend-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex flex-wrap items-center gap-2">
          💸 Werbekosten (30 Tage) — Google & Meta
          {data && (
            <span className="text-xs font-normal text-slate-500">
              Total CHF {data.total.toFixed(2)} · Google {data.total_google.toFixed(2)} · Meta {data.total_meta.toFixed(2)}
            </span>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        {loading && <div className="text-sm text-slate-400">Lade Werbekosten von Google & Meta…</div>}
        {data?.companies?.map((c) => (
          <div key={c.company_id} className="space-y-1" data-testid={`adspend-row-${c.company_id}`}>
            <div className="flex items-center justify-between text-xs">
              <span className="font-medium">{c.name}</span>
              <span className="text-slate-500">
                Google {c.google.connected ? `CHF ${c.google.cost.toFixed(2)}` : "—"} · Meta {c.meta.connected ? `CHF ${c.meta.spend.toFixed(2)}` : "—"} ·{" "}
                <b className="text-slate-800">CHF {c.total.toFixed(2)}</b>
              </span>
            </div>
            <div className="h-2.5 w-full bg-slate-100 rounded-full overflow-hidden flex">
              <div className="h-full bg-[#2563EB]" style={{ width: `${(c.google.cost / max) * 100}%` }} title={`Google CHF ${c.google.cost.toFixed(2)}`} />
              <div className="h-full bg-[#9333EA]" style={{ width: `${(c.meta.spend / max) * 100}%` }} title={`Meta CHF ${c.meta.spend.toFixed(2)}`} />
            </div>
          </div>
        ))}
        {data && (
          <div className="flex items-center gap-4 text-[10px] text-slate-500 pt-1">
            <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-[#2563EB] inline-block" /> Google Ads</span>
            <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-[#9333EA] inline-block" /> Meta (Facebook/Instagram)</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function GroupDashboardPage() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState("30");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (d) => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/group/overview`, { params: { days: d }, withCredentials: true });
      setData(r.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(days); }, [days, load]);

  const t = data?.totals;
  const maxSpend = Math.max(...(data?.companies || []).map((c) => (c.ads?.spend || 0) + (c.google_spend || 0)), 0);

  return (
    <div className="p-6 space-y-5" data-testid="group-dashboard-page">
      {/* Header band */}
      <div className="rounded-xl bg-white border border-slate-200 p-6 relative overflow-hidden">
        <div className="absolute right-0 top-0 h-full w-1.5 bg-[#2563EB]" />
        <div className="flex flex-wrap items-center gap-4">
          <img src="/invictaflow-logo.png" alt="InvictaFlow" className="w-12 h-12 rounded-xl" />
          <div className="flex-1">
            <div className="text-[10px] tracking-[0.3em] uppercase text-slate-400">InvictaX Group GmbH</div>
            <h1 className="text-2xl font-bold font-heading tracking-tight flex items-center gap-2">
              <Network className="w-5 h-5 text-[#2563EB]" /> Gruppen-Übersicht
            </h1>
            <p className="text-slate-500 text-sm">Alle Mandanten im InvictaFlow Marketing OS — Ebene 1</p>
          </div>
          <div className="flex items-center gap-2">
            <Select value={days} onValueChange={setDays}>
              <SelectTrigger className="w-36" data-testid="group-days-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="7">Letzte 7 Tage</SelectItem>
                <SelectItem value="30">Letzte 30 Tage</SelectItem>
                <SelectItem value="90">Letzte 90 Tage</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" size="icon" className="" onClick={() => load(days)} data-testid="group-refresh-btn">
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      <PageHelp
        title="InvictaX Gruppe"
        text="Ebene 1 des Marketing OS: alle Firmen aggregiert. Werbekosten und Leads kommen live von Meta (pro Firma eigenes Werbekonto). Klicke eine Firma auf, um ihre Standorte/Geschäftsbereiche (Ebene 3) zu sehen."
      />

      <AdSpendCard />

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-[var(--studio-text-muted)]" data-testid="group-loading">
          <Loader2 className="w-4 h-4 animate-spin" /> Lade Gruppendaten…
        </div>
      ) : data && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 xl:grid-cols-8 gap-3">
            <GroupKpi testid="group-kpi-spend" icon={Coins} label="Werbekosten" value={fmtMoney(t.ad_spend)} sub={`Meta ${fmtMoney(t.meta_spend)} · Google ${fmtMoney(t.google_spend)}`} />
            <GroupKpi testid="group-kpi-revenue" icon={Trophy} label="Umsatz (gewonnen)" value={fmtMoney(t.revenue)} sub={`${fmtNum(t.won_leads)} Aufträge`} />
            <GroupKpi testid="group-kpi-roas" icon={TrendingUp} label="ROAS" value={t.roas ? `${Number(t.roas).toFixed(2)}×` : "—"} sub={t.cost_per_lead ? `${fmtMoney(t.cost_per_lead)}/Lead` : null} />
            <GroupKpi testid="group-kpi-leads" icon={UserCheck} label="Leads" value={fmtNum(t.leads)} sub={t.form_leads > 0 ? `${fmtNum(t.form_leads)} via Formular` : null} />
            <GroupKpi testid="group-kpi-clicks" icon={MousePointerClick} label="Klicks (Ads)" value={fmtNum(t.clicks)} />
            <GroupKpi testid="group-kpi-published" icon={Send} label="Publizierte Posts" value={fmtNum(t.published)} />
            <GroupKpi testid="group-kpi-engagement" icon={Heart} label="Engagement" value={fmtNum(t.engagement)} />
            <GroupKpi testid="group-kpi-companies" icon={Building2} label="Firmen" value={fmtNum(t.companies)} />
          </div>

          <div className="space-y-2" data-testid="group-company-list">
            {data.companies.map((r) => (
              <CompanyRow key={r.company_id} r={r} maxSpend={maxSpend} />
            ))}
            {data.companies.length === 0 && (
              <Card><CardContent className="pt-5 pb-4 text-sm text-[var(--studio-text-muted)]">Noch keine Firmen angelegt.</CardContent></Card>
            )}
          </div>

          <WeeklyReportCard />
        </>
      )}
    </div>
  );
}
